// Front/src/pages/NewScale.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../style/NewScale.css';
import '../style/NewReport.css';
import { useAuthContext } from "../hooks/useAuthContext";
import { get_catalog_details_from_webapi, upload_scale, get_client_from_webapi, get_scale_from_webapi } from "../api";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from "react-helmet-async";
import { validateScaleData } from "../utils/scaleFormValidation";
import { generateScaleJson } from "../utils/generateScaleJson"; // to generate the json for the scale
import { useLanguageContext } from '../context/LanguageContext'; // for using right to left and left to right
import { useTranslate } from '../hooks/useTranslate'; // for translating to hebrew and english
import { isPositiveNumber } from "../utils/scaleFormValidation";
import { getRankAndMinFromNandD } from "../utils/mathHelper";  // for calculating rank and Min
import DivisionTable from './DivisionTable'; // New division table

const NewScale = () => {
    const location = useLocation(); // to use the data from previous pages
    const navState = location?.state || JSON.parse(localStorage.getItem("newScaleNavState") || "{}");
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const userId = user?.id || null;
    const [isCheckingClient, setIsCheckingClient] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showCreateScaleType, setShowCreateScaleType] = useState(false);

    const [scaleExists, setScaleExists] = useState(null);
    const [isCheckingScale, setIsCheckingScale] = useState(false);

    const [message, setMessage] = useState(""); // for showing messages on the screen
    const [messageType, setMessageType] = useState(""); 
    const [showDivisionTable, setShowDivisionTable] = useState(false);

    const [isCatalogValid, setIsCatalogValid] = useState(false);

    const t = useTranslate(); // Get translations
    const { language } = useLanguageContext(); // Get current language

    // store the info from new report
    useEffect(() => {
        if (location.state) {
            localStorage.setItem("newScaleNavState", JSON.stringify(location.state));
        }
    }, [location.state]);    

    const requiredFields = [
        // Scale auto-filled details
        "weightUnits",
        "accuracyClass",
        "dValue",
        "eValue",
        "maxValue",
        "minValue",
        "nValue",
    ];

    // for debugging
    // console.log("location.state = ", location.state);
    // console.log("navState?.MAKAT = ", navState?.MAKAT);
    // console.log("navState?.itemCode = ", navState?.itemCode);

    // store the user input for the scale data
    const savedScaleData = localStorage.getItem("newScaleFormData");

    const [formData, setFormData] = useState(() => {
        const defaultData = {
          serialNumber: navState?.serialId || "",
          internalId: (navState?.SERN || "").toString(),
          clientId: navState?.custId || "",
          catalogNumber: navState?.itemCode,
          model: '',
          manufacturer: '',
          weightUnits: '',
          accuracyClass: '',
          dValue: '',
          eValue: '',
          maxValue: '',
          minValue: '',
          nValue: '',
          notes: ''
        };
      
        return savedScaleData ? { ...defaultData, ...JSON.parse(savedScaleData) } : defaultData;
    });      

    const getFieldClass = (name) => {
        const val = formData[name];
        const d = parseFloat(formData.dValue);
        const e = parseFloat(formData.eValue);
    
        if (requiredFields.includes(name) && (!val || val.toString().trim() === "")) {
            return "invalid-input";
        }
    
        if (name === "dValue" && !isPositiveNumber(val)) {
            return "invalid-input";
        }
    
        if (name === "eValue") {
            if (!isPositiveNumber(val) || (!isNaN(d) && !isNaN(e) && e < d)) {
                return "invalid-input";
            }
        }
    
        return "";
    };
    

    // Check client as soon as we enter new scale addition
    useEffect(() => {
        if (formData.clientId?.trim()) {
            handleCheckClient();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check catalog number as soon as we enter new scale addition
    useEffect(() => {
        if (formData.catalogNumber?.trim()) {
            handleCheckCatalog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Press check scale when entering the page
    useEffect(() => {
        if (formData.serialNumber?.trim()) {
            handleCheckScale();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
    
        setFormData((prevData) => {
            let updatedData = {
                ...prevData,
                [name]: name === "catalogNumber" ? value.toUpperCase() : value
            };

            if (name === "catalogNumber") {
                updatedData.catalogNumber = value.toUpperCase();
                setIsCatalogValid(false); // invalidate if changed
            }            
    
            // Update nValue automatically when maxValue or eValue changes
            if (name === "maxValue" || name === "eValue") {
                const max = parseFloat(name === "maxValue" ? value : prevData.maxValue);
                const e = parseFloat(name === "eValue" ? value : prevData.eValue);
    
                if (!isNaN(max) && !isNaN(e) && e !== 0) {
                    updatedData.nValue = Math.round(max / e).toString();
                } else {
                    updatedData.nValue = "";
                }
            }
    
            return updatedData;
        });
    };
    
    // Post error to avoid code duplications
    const PostError = (message) => {
        toast.error(message, {
        icon: false, // Removes the default tick icon
        autoClose: 3000, // 3 seconds (default)
    });
        // For debugging
        setMessage(message);
        setMessageType("error");
        console.error(message);
    };    

    const handleCheckClient = async () => {

        // reset message
        setMessage("");
        setMessageType("");

        if (!formData.clientId.trim()) {
            PostError(t.scale_client_id_required);
            return;
        }
    
        setIsCheckingClient(true);
    
        try {
            // Get client from webapi
            const client_response = await get_client_from_webapi(userId, formData.clientId);
    
            if (client_response && client_response.status === 200) {
                toast.success(t.clientIdExists, {        
                    icon: false, // Removes the default tick icon
                    autoClose: 3000, // 3 seconds (default)
                });
            } else {
                const errorMsg = client_response?.error?.response?.data || "Unknown error";
                PostError(`Error: ${errorMsg}`);
            }
        } catch (error) {
            PostError("Error:" + (error?.response?.data || "Unknown error"));
        } finally {
            setIsCheckingClient(false);
        }
    };
    
    // new check scale button for note #1
    const handleCheckScale = async () => {

        // for debugging
        setMessage("");
        setMessageType("");
    
        if (!formData.serialNumber.trim()) {
            PostError(t.errorScaleIdEmpty);
            return;
        }
    
        setIsCheckingScale(true);
    
        try {
            const scaleData = await get_scale_from_webapi(userId, formData.serialNumber);
            
            // for debugging
            //console.log("scaleData = ", scaleData);

            if (!scaleData?.ScaleType) {
                toast.success(t.serialAvailable, {
                    icon: false,
                    autoClose: 3000,
                });    
                setScaleExists(false);
                setIsCheckingScale(false);
                return;
            }
            
            else if (scaleData.Owner.Id !== formData.clientId) {
                PostError(t.errorScaleOtherClientSerial);
                setScaleExists(true);
                setIsCheckingScale(false);
                return;
            }
    
            PostError(t.serialAlreadyExists);
            setScaleExists(true);
    
        } catch (error) {
            PostError(`${t.errorCheckingScale} ${error.message}`);
            setScaleExists(false);
        } finally {
            setIsCheckingScale(false);
        }
    };
    
    const handleCheckCatalog = async () => {

        // reset message
        setMessage("");
        setMessageType("");
        setIsCatalogValid(true);
        setShowCreateScaleType(false); // reset

        if (!formData.catalogNumber.trim()) {
            PostError(t.scale_catalog_required);
            return;
        }

        setIsChecking(true);

        try {
            const catalog_response = await get_catalog_details_from_webapi(userId, formData.catalogNumber);

            // for debugging
            // console.log("catalog_response = ",catalog_response);
            // console.log("catalog_response?.data?.ScaleAccuracy?.D = ", catalog_response?.data?.ScaleAccuracy?.D);


            if (catalog_response && catalog_response.status === 200) {
                toast.success(t.catalogSuccess, {        
                    icon: false, // Removes the default tick icon
                    autoClose: 3000, // 3 seconds (default)
                });
                setFormData(prevData => ({
                    ...prevData,
                    model: catalog_response?.data?.ModelName?.HebrewString ?? "",
                    manufacturer: catalog_response?.data?.ManufacturerName?.HebrewString ?? "",
                    weightUnits: catalog_response?.data?.ScaleAccuracy?.WeightUnits ?? "",
                    accuracyClass: Number(4 - catalog_response?.data?.ScaleAccuracy?.AccuracyClass) ?? "",
                    dValue: catalog_response?.data?.ScaleAccuracy?.D ?? "",
                    eValue: catalog_response?.data?.ScaleAccuracy?.E ?? "",
                    maxValue: catalog_response?.data?.ScaleAccuracy?.Max ?? "",
                    minValue: catalog_response?.data?.ScaleAccuracy?.Min ?? "",
                    nValue: catalog_response?.data?.ScaleAccuracy?.N ?? ""
                }));
                setShowCreateScaleType(false); // hide create button if found
            } else {
                const errorMsg = catalog_response?.error?.response?.data || t.unknownError;
                PostError(`Error: ${errorMsg}`);
                setShowCreateScaleType(true);
                setIsCatalogValid(false);
            }
        } catch (error) {
            PostError("Error:" + (error?.response?.data || t.unknownError));
            setShowCreateScaleType(true);
            setIsCatalogValid(false);
        } finally {
            setIsChecking(false);
        }
    };

    // effect to calculate rank and accuracy class
    useEffect(() => {
        const n = parseFloat(formData.nValue);
        const d = parseFloat(formData.dValue);
    
        if (!isNaN(n) && !isNaN(d) && n > 0 && d > 0) {
            const result = getRankAndMinFromNandD(n, d);
            if (result) {
                setFormData(prev => {
                    return {
                        ...prev,
                        accuracyClass: result.accuracyClass,
                        minValue: prev.minValue?.toString().trim() === "" 
                            ? result.min.toString() 
                            : prev.minValue
                    };
                });
            }
        }
    }, [formData.nValue, formData.dValue]);    

    const handleSubmit = async (e) => {

        // reset message
        setMessage("");
        setMessageType("");

        e.preventDefault();

        if (!isCatalogValid) {
            PostError(t.scale_catalog_required_check); // check if vatalog is valid
            return;
        }
        
        const validationError = validateScaleData(formData, t);
        if (validationError) {
            PostError(validationError);
            return;
        }

        setIsUploading(true);

        try {
            const scaleJson = generateScaleJson(formData, userId); // generate the proper JSON
            const uploadResponse = await upload_scale(scaleJson, userId); // send the generated object
            
            // for debugging
            // console.log("uploadResponse ==", uploadResponse);

            if (uploadResponse && uploadResponse.status === 200) {
                localStorage.removeItem("newScaleFormData"); // Clear saved form data
                toast.success(t.scale_success, {
                    onClose: () => navigate('/newreport', {
                        replace: true,
                        state: {
                            ...location.state,
                            DOCNO: navState?.DOCNO,
                            SERNUM: navState?.SERNUM,
                            KLINE: navState?.KLINE,
                            SERN: navState?.SERN,
                            MAKAT: navState?.MAKAT,
                            itemCode: navState?.itemCode,
                            custId: navState?.custId,
                            address: navState?.address,
                            SAMLIL: navState?.SAMLIL,
                            fromSearch: navState?.fromSearch,
                            serialNumber: formData.serialNumber,
                            isSymboled: navState?.isSymboled,
                        }
                    }),
                    autoClose: 3000, // 3 seconds (default)
                    icon: false, // Removes the default tick icon
                });
            } else {
                PostError(t.scale_upload_error);
            }
        } catch (error) {
            PostError(t.scale_submit_error + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    useEffect(() => {
        localStorage.setItem("newScaleFormData", JSON.stringify(formData));
    }, [formData]);
      
    return (
        <div className="new-scale-container" data-theme={document.body.classList.contains('dark') ? 'dark' : 'light'} dir={language === 'he' ? 'rtl' : 'ltr'}>
            <Helmet>
                <title>Shekel Technet - New Scale</title>
            </Helmet>

            <button 
                type="button" 
                className="button-go-back"
                onClick={() => {
                    localStorage.removeItem("newScaleFormData"); // Clear scale session
                    navigate('/newreport', {
                        replace: true,
                        state: {
                            ...navState,
                            serialNumber: formData.serialNumber,
                            isSymboled: navState?.isSymboled
                        }
                    });
                }}
            >
                {t.goBack}
            </button>


            <h2>{t.addScale}</h2>
            <form onSubmit={handleSubmit}>

                <label>{t.clientNumber}</label>
                <div className="catalog-check-container">
                    <input
                        type="text"
                        name="clientId"
                        value={formData.clientId}
                        onChange={handleChange}
                        placeholder={t.enterClientID}
                        className="numeric-input"
                    />
                    <button
                        type="button"
                        onClick={handleCheckClient}
                        disabled={isCheckingClient}
                    >
                        {isCheckingClient ? t.checking : t.check}
                    </button>
                </div>

                <label>{t.serial}</label>
                <div className="catalog-check-container">
                    <input
                        type="text"
                        name="serialNumber"
                        value={formData.serialNumber}
                        onChange={handleChange}
                        placeholder={t.enterSerialNumber}
                        className={`numeric-input ${scaleExists === true ? 'invalid-input' : ''}`}
                    />
                    <button
                        type="button"
                        onClick={handleCheckScale}
                        disabled={isCheckingScale}
                    >
                        {isCheckingScale ? t.checking : t.check}
                    </button>
                </div>

                <label>{t.internalId}</label>
                <input
                    type="text"
                    name="internalId"
                    value={formData.internalId}
                    onChange={handleChange}
                    placeholder={t.enterInternalId}
                    className="numeric-input"
                />
                
                <label>{t.catalogNumber}</label>
                <div className="catalog-check-container">
                <input
                    type="text"
                    name="catalogNumber"
                    value={formData.catalogNumber}
                    onChange={handleChange}
                    placeholder={t.enterCatalogNumber}
                    className="numeric-input"
                />

                    <button type="button" onClick={handleCheckCatalog} disabled={isChecking}>
                        {isChecking ? t.checking : t.check}
                    </button>
                </div>
                
                {showCreateScaleType && (
                    <button
                        type="button"
                        onClick={() => navigate('/newscaletype', { state: { ...navState, catalogNumber: formData.catalogNumber, isSymboled: navState?.isSymboled } })}
                        className="create-scale-btn"
                    >
                        {t.createNewScaleType || "Create New Scale Type"}
                    </button>
                )}

                <label>{t.model}</label>
                <input 
                    type="text" 
                    name="model" 
                    value={formData.model} 
                    onChange={handleChange}
                    className="numeric-input"
                />

                <label>{t.manufacturer}</label>
                <input 
                    type="text" 
                    name="manufacturer" 
                    value={formData.manufacturer} 
                    onChange={handleChange}
                    className="numeric-input"
                />

                {formData.weightUnits != null && formData.weightUnits !== "" && (
                    <>
                        <label>{t.weightUnit}</label>
                        <select name="weightUnits" value={formData.weightUnits} onChange={handleChange}>
                            <option value="0">{t.miligram}</option>
                            <option value="1">{t.gram}</option>
                            <option value="2">{t.kilogram}</option>
                            <option value="3">{t.lb}</option>
                        </select>

                        <button 
                            type="button" 
                            onClick={() => setShowDivisionTable(prev => !prev)}
                            className="toggle-division-table-button"
                        >
                            {showDivisionTable ? t.hideDivisionTable : t.showDivisionTable}
                        </button>

                        {showDivisionTable && <DivisionTable />}
                        
                        <label>{t.accuracyClass}</label>
                        <select
                            name="accuracyClass"
                            value={
                                formData.accuracyClass === 1 ? "One" :
                                formData.accuracyClass === 2 ? "Two" :
                                formData.accuracyClass === 3 ? "Three" :
                                formData.accuracyClass === 4 ? "Four" : ""
                            }
                            onChange={(e) => setFormData(prevData => ({
                                ...prevData,
                                accuracyClass:
                                    e.target.value === "One" ? 1 :
                                    e.target.value === "Two" ? 2 :
                                    e.target.value === "Three" ? 3 :
                                    e.target.value === "Four" ? 4 : ""
                            }))}
                        >
                            <option value="">{t.selectAccuracyClass}</option>
                            <option value="One">{t.one}</option>
                            <option value="Two">{t.two}</option>
                            <option value="Three">{t.three}</option>
                            <option value="Four">{t.four}</option>
                        </select>

                        
                        <label>{t.dValue}</label>
                        <input 
                            type="number" 
                            name="dValue" 
                            value={formData.dValue} 
                            onChange={handleChange} 
                            className={`numeric-input ${getFieldClass("dValue")}`}
                        />
                        
                        <label>{t.eValue}</label>
                        <input 
                            type="number" 
                            name="eValue" 
                            value={formData.eValue} 
                            onChange={handleChange} 
                            className={`numeric-input ${getFieldClass("eValue")}`}
                        />
                        
                        <label>{t.maxValue}</label>
                        <input 
                            type="number" 
                            name="maxValue" 
                            value={formData.maxValue} 
                            onChange={handleChange} 
                            className={`numeric-input ${getFieldClass("maxValue")}`}
                        />
                       
                        <label>{t.minValue}</label>
                        <input
                            type="number"
                            name="minValue"
                            value={formData.minValue}
                            onChange={handleChange}
                            className={`numeric-input ${getFieldClass("minValue")}`}
                        />
                        
                        <label>{t.nValue}</label>
                        <input 
                            type="number" 
                            name="nValue" 
                            value={formData.nValue} 
                            readOnly // User cannot edit manually
                            className={`numeric-input ${getFieldClass("nValue")}`}
                        />

                    </>
                )}

                {message && (
                    <div className={`alert ${messageType === "error" ? "error" : "success"}`}>
                        {message}
                    </div>
                )}

                <label>{t.generalComments}</label>
                <input
                    type="text"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t.enterComments}
                />

                <div className="button-container">
                    <button 
                        type="submit" 
                        disabled={isUploading || scaleExists === true}
                        className={scaleExists === true ? 'disabled-button' : ''}
                    >
                        {isUploading ? t.submitButtonUploading : t.submitScale}
                    </button>
                </div>

            </form>
            {/* PopUp Notifications using Toast */}
            <ToastContainer
                position="top-center"
                autoClose={3000}
                toastClassName="custom-toast"
                closeButton={true} // use default button
            />
        </div>
    );
};

export default NewScale;
